import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { esignatureDocumentService } from "../services/esignatureDocumentService";
import { esignatureDocumentDisplayId } from "./EsignatureDocumentCommonStore";
import { EsignatureDocument } from "../../../api/graphql";
import { getEsignatureRecipientValues } from "../../esignatureRecipient/store/EsignatureRecipientCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { EsignatureProvider } from "../../../api/graphql";
import { EsignatureDocumentStatus } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class EsignatureDocumentViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = esignatureDocumentService;
  esignatureDocumentInstance: EsignatureDocument | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  externalIdValue: string = "";
  providerValue: EsignatureProvider = "" as EsignatureProvider;
  nameValue: string = "";
  statusValue: EsignatureDocumentStatus = "" as EsignatureDocumentStatus;
  sentAtValue: Date | null = null;
  completedAtValue: Date | null = null;
  signedDocumentFiles: CrudRows = {};
  sourceEntityTypeValue: string = "";
  sourceEntityIdValue: string = "";
  recipientsColumnWidths: Record<string, number | string> = {};
  recipientsRows: CrudRows = {};
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.recipientsColumnWidths = {
      name: 200,
      email: 200,
      role: 200,
      status: 200,
      signedAt: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEsignatureDocumentById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.esignatureDocumentOne({ id });
    this.esignatureDocumentInstance = result.esignatureDocumentOne;
    if (this.esignatureDocumentInstance) {
      this.loadValues(this.esignatureDocumentInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.esignatureDocumentInstance) {
      return esignatureDocumentDisplayId(
        this.esignatureDocumentInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EsignatureDocumentSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getProviderOptions() {
    return this.langStore.EsignatureProviderOptions;
  }

  getStatusOptions() {
    return this.langStore.EsignatureDocumentStatusOptions;
  }

  get recipientsVisibleColumns() {
    return [
      {
        name: "name",
        title: this.langStore.EsignatureRecipientNameLabel,
        width: this.recipientsColumnWidths["name"],
      },
      {
        name: "email",
        title: this.langStore.EsignatureRecipientEmailLabel,
        width: this.recipientsColumnWidths["email"],
      },
      {
        name: "role",
        title: this.langStore.EsignatureRecipientRoleLabel,
        width: this.recipientsColumnWidths["role"],
      },
      {
        name: "status",
        title: this.langStore.EsignatureRecipientStatusLabel,
        width: this.recipientsColumnWidths["status"],
      },
      {
        name: "signedAt",
        title: this.langStore.EsignatureRecipientSignedAtLabel,
        width: this.recipientsColumnWidths["signedAt"],
      },
    ];
  }

  recipientsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.recipientsColumnWidths = values;
  }

  get recipientsRowsDyn() {
    return this.recipientsRows;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "EsignatureDocument",
      fieldName: fieldName,
      instanceId: this.esignatureDocumentInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "EsignatureDocument",
        fieldName: fieldName,
        instanceId: this.esignatureDocumentInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.esignatureDocumentInstance = null;
    this.externalIdValue = "";
    this.providerValue = "" as EsignatureProvider;
    this.nameValue = "";
    this.statusValue = "" as EsignatureDocumentStatus;
    this.sentAtValue = null;
    this.completedAtValue = null;
    this.signedDocumentFiles = {};
    this.sourceEntityTypeValue = "";
    this.sourceEntityIdValue = "";
    this.recipientsRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEsignatureDocument() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editEsignatureDocument() {
    const id = this.esignatureDocumentInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/esignatureDocument/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEsignatureDocument(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEsignatureDocument(): any {
    yield this.close();
  }

  loadValues(instance: EsignatureDocument) {
    this.idValue = instance.id;
    this.externalIdValue = instance.externalId;
    this.providerValue = instance.provider;
    this.nameValue = instance.name;
    this.statusValue = instance.status;
    this.sentAtValue = instance.sentAt ? new Date(instance.sentAt) : null;
    this.completedAtValue = instance.completedAt
      ? new Date(instance.completedAt)
      : null;
    this.signedDocumentFiles = instance.signedDocument_data
      ? {
          [instance.signedDocument_data.name]: {
            name: instance.signedDocument_data.name,
            formattedSize: formatBytes(instance.signedDocument_data?.size || 0),
            mimetype: instance.signedDocument_data?.mimetype,
            id: instance.signedDocument_data.name,
            __status__: "original",
          },
        }
      : {};
    this.sourceEntityTypeValue = instance.sourceEntityType || "";
    this.sourceEntityIdValue = instance.sourceEntityId || "";
    this.recipientsRows = instance.recipients
      ? Object.assign(
          {},
          ...instance.recipients.map((x) => {
            const processedRow = getEsignatureRecipientValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEsignatureDocumentById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
